import React, { useState, useEffect } from 'react';
import { 
  Crown, 
  Check, 
  Zap, 
  Star, 
  CreditCard, 
  Calendar, 
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  Clock,
  Download,
  XCircle,
  BarChart2,
  Shield,
  X
} from 'lucide-react';
import { useAuth } from '../components/auth/AuthComponents';
import { useUsage } from '../contexts/UsageContext';
import { API_BASE, apiRequest } from '../utils/api';
import { toast } from 'react-toastify';
import ContactSupportForm from '../components/shared/ContactSupportForm';
import useIsMobile from '../hooks/useIsMobile';
import {
  getFreePlan,
  getPaidPlans,
  publicPlanLabel,
  getDocumentProcessingLabel,
  getUsageDisplayValues,
} from '../utils/planLabels';

const ConfirmModal = ({ open, title, message, onCancel, onConfirm }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 flex flex-col justify-center" style={{ marginTop: 0 }}>
        <div className="flex flex-col items-center">
          <Shield className="w-10 h-10 text-yellow-500 mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-700 mb-6 text-center">{message}</p>
          <div className="flex space-x-4">
            <button
              onClick={onCancel}
              className="px-5 py-2 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-5 py-2 rounded-lg brand-gradient-btn font-semibold  transition"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionPage = ({ setCurrentPage }) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { usage, subscription, refreshUsage } = useUsage();
  const [plans, setPlans] = useState([]);
  const [currentSubscriptions, setCurrentSubscriptions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activeTab, setActiveTab] = useState('my-subscription');
  const [message, setMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({ open: false, planId: null, planName: '', planSlug: '' });
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  const [retryingPaymentId, setRetryingPaymentId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []); // Remove dependencies to prevent infinite refresh

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'plans') {
      setActiveTab('plans');
    } else if (tab === 'payment-history') {
      setActiveTab('payment-history');
    } else {
      setActiveTab('my-subscription');
    }
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Update currentSubscriptions when subscription data changes
  useEffect(() => {
    if (subscription?.subscriptions) {
      setCurrentSubscriptions(subscription.subscriptions);
    }
  }, [subscription]);

  // Debug usage data

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      const [plansData, historyData] = await Promise.all([
        apiRequest('subscription/plans').catch(() => ({ plans: [] })),
        apiRequest('subscription/payment-history').catch(() => ({ payments: [] })),
      ]);
      
      setPlans(plansData.plans || []);
      setCurrentSubscriptions(subscription?.subscriptions || []);
      setPaymentHistory(historyData.payments || []);

    } catch (error) {
      toast.error('Failed to load subscription data.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId, planSlug, planLabel) => {
    setConfirmModal({ open: true, planId, planName: planLabel, planSlug });
  };

  const doUpgrade = async () => {
    setConfirmModal({ open: false, planId: null, planName: '', planSlug: '' });
    setSubscribingPlanId(confirmModal.planId);
    try {
      // 1. Upgrade/Create the subscription
      await apiRequest(`subscription/upgrade/${confirmModal.planId}`, {
        method: 'POST'
      });

      // 2. Initiate payment
      const paymentResponse = await apiRequest(`payments/initiate`, {
        method: 'POST',
        body: {
          plan_name: confirmModal.planSlug,
          billing_cycle: billingCycle,
          redirect_url: `${window.location.origin}/payment-result`
        }
      });

      if (paymentResponse.payment_link) {
        window.location.href = paymentResponse.payment_link;
      } else {
        toast.error('Failed to get payment link. Please try again.');
        setSubscribingPlanId(null);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upgrade subscription or initiate payment.');
      setSubscribingPlanId(null);
    }
  };

  const isRetryablePayment = (payment) =>
    ['failed', 'cancelled', 'pending', 'unpaid'].includes(
      String(payment?.status || '').toLowerCase(),
    );

  const resolvePaymentPlanName = (payment) => {
    if (payment?.plan_name) return payment.plan_name;
    if (payment?.plan_id) {
      const byId = plans.find((p) => p.id === payment.plan_id);
      if (byId?.name) return byId.name;
    }
    const sub = currentSubscriptions.find(
      (s) => s.subscription_id === payment?.subscription_id,
    );
    if (sub?.plan_name) return sub.plan_name;
    if (sub?.plan_id) {
      const bySubPlan = plans.find((p) => p.id === sub.plan_id);
      if (bySubPlan?.name) return bySubPlan.name;
    }
    return null;
  };

  const initiateExistingSubscriptionPayment = async ({ planName, billingCycle: cycle, paymentId }) => {
    if (!planName) {
      toast.error('This payment is missing its subscription plan. Please contact support.');
      return;
    }

    setRetryingPaymentId(paymentId);
    try {
      const paymentResponse = await apiRequest('payments/initiate', {
        method: 'POST',
        body: {
          plan_name: planName,
          billing_cycle: cycle === 'yearly' ? 'yearly' : 'monthly',
          redirect_url: `${window.location.origin}/payment-result`,
        },
      });

      if (!paymentResponse.payment_link) {
        throw new Error('Failed to get payment link. Please try again.');
      }
      window.location.href = paymentResponse.payment_link;
    } catch (error) {
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setRetryingPaymentId(null);
    }
  };

  const handleRetryPayment = (payment) => initiateExistingSubscriptionPayment({
    planName: resolvePaymentPlanName(payment),
    billingCycle: payment.billing_cycle,
    paymentId: payment.id,
  });

  const handlePayNow = (subscription) => initiateExistingSubscriptionPayment({
    planName: subscription.plan_name,
    billingCycle: subscription.billing_cycle,
    paymentId: `subscription-${subscription.subscription_id}`,
  });

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? This will take effect at the end of your current billing period.')) return;

    try {
      const response = await apiRequest('subscription/cancel', {
        method: 'POST',
      });
      toast.success(response.message);
      fetchSubscriptionData(); // Refresh data after cancellation
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription.');
    }
  };

  const formatPrice = (price) => {
    return `₦${(price / 100).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getPlanColor = (planName) => {
    switch (planName?.toLowerCase()) {
      case 'free': return { bg: 'bg-gray-500/10', text: 'text-[var(--color-text-secondary)]', border: 'border-gray-500/20' };
      case 'starter': return { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' };
      case 'professional': return { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' };
      case 'enterprise': return { bg: 'bg-[var(--color-primary-soft)]', text: 'text-[var(--color-primary)]', border: 'border-[var(--color-primary)]/20' };
      default: return { bg: 'bg-gray-500/10', text: 'text-[var(--color-text-muted)]', border: 'border-gray-500/20' };
    }
  };

  const renderPlanFeatures = (plan) => (
    <ul className="space-y-3 mb-8 text-[var(--color-text-secondary)]">
      <li className="flex items-center">
        <Check className="w-4 h-4 mr-3 text-green-400" />{' '}
        {getDocumentProcessingLabel(plan)}
      </li>
      {plan.features &&
        Object.entries(plan.features).map(([feature, enabled]) => (
          <li key={feature} className={`flex items-center ${enabled ? '' : 'text-[var(--color-text-muted)] line-through'}`}>
            {enabled ? (
              <Check className="w-4 h-4 mr-3 text-green-400" />
            ) : (
              <X className="w-4 h-4 mr-3 text-red-500" />
            )}
            {feature.replace(/_/g, ' ')}
          </li>
        ))}
    </ul>
  );

  const handleDownloadInvoice = async (paymentId) => {
    try {
      setDownloadingInvoiceId(paymentId);
      const token = localStorage.getItem('docxsai-token');
      if (!token) {
        toast.error('Please log in to download invoices');
        setDownloadingInvoiceId(null);
        return;
      }
      
      const response = await fetch(`${API_BASE}/payments/invoice/${paymentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Invoice download error:', response.status, errorText);
        throw new Error(`Failed to download invoice: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${paymentId}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully!');
    } catch (err) {
      console.error('Invoice download error:', err);
      toast.error(err.message || 'Failed to download invoice.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const renderPlansView = () => {
    const planHierarchy = {
      starter: 1,
      professional: 2,
      enterprise: 3,
    };

    const currentPlanLevel =
      currentSubscriptions.length > 0 ? planHierarchy[currentSubscriptions[0].plan_name] : 0;
    const isOnFreePlan = currentPlanLevel === 0;
    const freePlan = getFreePlan(plans);
    const paidPlans = getPaidPlans(plans);

    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center mb-6">
          <div className="bg-[var(--color-surface-muted)] rounded-xl p-1.5 flex items-center">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${billingCycle === 'monthly' ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors relative ${billingCycle === 'yearly' ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'}`}
            >
              Yearly
              <span className="absolute -top-2 -right-2 text-xs bg-green-500 text-[var(--color-text)] px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {freePlan ? (
            <div
              className={`elevated-card border rounded-2xl p-6 relative transition-all hover:border-[var(--color-border-subtle)] ${
                isOnFreePlan ? 'border-[var(--color-primary)]/60' : 'border-[var(--color-border)]'
              }`}
            >
              {isOnFreePlan ? (
                <div className="absolute top-4 right-4 bg-[var(--color-primary-soft)] text-[var(--color-primary)] text-xs font-medium px-2 py-1 rounded-full">
                  Current Plan
                </div>
              ) : null}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-[var(--color-text)] mb-2">{publicPlanLabel(freePlan)}</h3>
                <div className="text-3xl font-bold text-[var(--color-text)]">Free</div>
              </div>
              {renderPlanFeatures(freePlan)}
              <button
                disabled
                className="w-full py-2.5 px-4 rounded-lg font-semibold bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] cursor-not-allowed"
              >
                {isOnFreePlan ? 'Your Plan' : 'Included'}
              </button>
            </div>
          ) : null}
          {paidPlans.map((plan) => {
            const isCurrent = currentSubscriptions.some((sub) => sub.plan_name === plan.name);
            const thisPlanLevel = planHierarchy[plan.name];
            const isPaidUser = currentPlanLevel > 0;
            const isUpgrade = thisPlanLevel > currentPlanLevel;
            const isDowngrade = thisPlanLevel < currentPlanLevel;
            const shouldEnable = !isCurrent && (!isPaidUser || isUpgrade);
            const isEnterprise = plan.name.toLowerCase() === 'enterprise';
            const actionDisabled =
              !shouldEnable || subscribingPlanId === plan.id;
            return (
              <div
                key={plan.id}
                className={`elevated-card border rounded-2xl p-6 relative transition-all hover:border-[var(--color-border-subtle)] ${
                  isCurrent ? 'border-[var(--color-primary)]/60' : 'border-[var(--color-border)]'
                }`}
              >
                {isCurrent ? (
                  <div className="absolute top-4 right-4 bg-[var(--color-primary-soft)] text-[var(--color-primary)] text-xs font-medium px-2 py-1 rounded-full">
                    Current Plan
                  </div>
                ) : null}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[var(--color-text)] mb-2">{publicPlanLabel(plan)}</h3>
                  <div className="text-3xl font-bold text-[var(--color-text)]">
                    {isEnterprise
                      ? 'Custom'
                      : formatPrice(
                          billingCycle === 'monthly' ? plan.monthly_price : plan.yearly_price,
                        )}
                    {!isEnterprise ? (
                      <span className="text-base font-normal text-[var(--color-text-muted)]">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    ) : null}
                  </div>
                </div>
                {renderPlanFeatures(plan)}
                <button
                  onClick={() => {
                    if (isEnterprise) {
                      setShowContactModal(true);
                    } else {
                      handleUpgrade(plan.id, plan.name, publicPlanLabel(plan));
                    }
                  }}
                  disabled={actionDisabled}
                  className={`w-full py-2.5 px-4 rounded-lg font-semibold transition-all flex items-center justify-center ${
                    !shouldEnable
                      ? 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] cursor-not-allowed'
                      : isEnterprise
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-[var(--color-text)]'
                        : 'brand-gradient-btn'
                  } disabled:opacity-50`}
                >
                  {subscribingPlanId === plan.id ? (
                    <svg className="animate-spin w-5 h-5 mr-2 text-[var(--color-text)]" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : null}
                  {isEnterprise
                    ? 'Contact Us'
                    : isCurrent
                      ? 'Your Plan'
                      : isDowngrade
                        ? 'Downgrade Not Available'
                        : subscribingPlanId === plan.id
                          ? 'Processing...'
                          : 'Subscribe'}
                </button>
              </div>
            );
          })}
        </div>
        <ConfirmModal
          open={confirmModal.open}
          title="Subscribe to Plan"
          message={`Are you sure you want to subscribe to "${confirmModal.planName}"? Payments are processed by TabsolTech for DocxsAi. Your statement may show TabsolTech as the merchant.`}
          onCancel={() => setConfirmModal({ open: false, planId: null, planName: '', planSlug: '' })}
          onConfirm={doUpgrade}
        />
        {showContactModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm">
            <div className="bg-[var(--color-surface)] rounded-xl shadow-2xl p-6 max-w-lg w-full relative text-left">
              <button
                className="absolute top-3 right-3 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                onClick={() => setShowContactModal(false)}
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-[var(--color-text)] mb-4 text-left">Contact Us About Enterprise Plan</h2>
              <ContactSupportForm
                initialName={user?.full_name || ''}
                initialEmail={user?.email || ''}
                onClose={() => setShowContactModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentSubscriptionView = () => {
    if (!currentSubscriptions || currentSubscriptions.length === 0) {
      return (
        <div className="text-center py-12">
          <h3 className="text-xl text-[var(--color-text)] mb-2">No active subscription</h3>
          <p className="text-[var(--color-text-muted)] mb-4">You are currently on the free plan.</p>
          <button
            onClick={() => setActiveTab('plans')}
            className="brand-gradient-btn px-6 py-2 rounded-lg font-medium"
          >
            View Plans
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-8">
        {currentSubscriptions.map((sub) => {
          const plan = plans.find(p => p.id === sub?.plan_id);
          const planColors = getPlanColor(sub.plan_name);
          const isInactiveUnpaid = sub.subscription_status === 'inactive' && sub.plan_name !== 'free';
          const usageDisplay = getUsageDisplayValues(usage);
          const isFreeSub = sub.plan_name === 'free';
          const used = isFreeSub ? usageDisplay.used : (sub.total_this_month || 0);
          const limit = isFreeSub
            ? (usageDisplay.limit || 2)
            : (sub.custom_limit != null ? sub.custom_limit : sub.monthly_limit);
          const periodLabel = isFreeSub || usageDisplay.isDaily ? 'today' : 'this month';
          const usagePercentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
          return (
            <div key={sub.subscription_id} className={`elevated-card border ${planColors.border} rounded-2xl p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-xl font-bold ${planColors.text}`}>{publicPlanLabel(sub.plan_name)}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${sub.subscription_status === 'active' ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'}`}>
                  {sub.subscription_status.charAt(0).toUpperCase() + sub.subscription_status.slice(1)}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <div className="text-[var(--color-text-muted)] text-sm">
                  <span>Period: </span>
                  <span>{sub.current_period_start ? formatDate(sub.current_period_start) : 'N/A'} - {sub.current_period_end ? formatDate(sub.current_period_end) : 'N/A'}</span>
                </div>
                <div className="text-[var(--color-text-muted)] text-sm">
                  <span>Billing: </span>
                  <span className="capitalize">{sub.billing_cycle || 'monthly'}</span>
                </div>
              </div>
              {isInactiveUnpaid ? (
                <div className="mt-4 rounded-lg border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 p-4">
                  <p className="text-sm font-medium text-[var(--color-warning)]">
                    This plan is awaiting payment, not expired. Pay now to activate it.
                  </p>
                  <button
                    type="button"
                    onClick={() => handlePayNow(sub)}
                    disabled={retryingPaymentId === `subscription-${sub.subscription_id}`}
                    className="mt-3 inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {retryingPaymentId === `subscription-${sub.subscription_id}`
                      ? 'Opening payment...'
                      : 'Pay now'}
                  </button>
                </div>
              ) : null}
                {/* Usage and stats */}
                <div className="mt-4">
                  <div className="mb-2 text-sm text-[var(--color-text-secondary)]">
                    Usage
                    <span className="ml-2 text-[var(--color-text)] font-semibold">
                      {used} / {limit} documents used {periodLabel}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="flex items-center justify-between bg-[var(--color-surface-muted)] rounded-lg px-4 py-2">
                      <span className="text-[var(--color-text-secondary)]">Total Contracts</span>
                      <span className="font-bold text-[var(--color-text)]">{sub.contracts_this_month || 0}</span>
                    </div>
                    <div className="flex items-center justify-between bg-[var(--color-surface-muted)] rounded-lg px-4 py-2">
                      <span className="text-[var(--color-text-secondary)]">Total CVs</span>
                      <span className="font-bold text-[var(--color-text)]">{sub.cvs_this_month || 0}</span>
                    </div>
                    <div className="flex items-center justify-between bg-[var(--color-surface-muted)] rounded-lg px-4 py-2">
                      <span className="text-[var(--color-text-secondary)]">Total SOPs</span>
                      <span className="font-bold text-[var(--color-text)]">{sub.sops_this_month || 0}</span>
                    </div>
                  </div>
                </div>
            </div>
          );
        })}
        <div className="elevated-card p-6 rounded-2xl">
          <h4 className="text-lg font-semibold text-[var(--color-text)] mb-4">Manage Subscription</h4>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
            <button
              onClick={() => setActiveTab('plans')}
              className="w-full sm:w-auto flex-1 brand-gradient-btn px-6 py-3 rounded-xl"
            >
              Subscribe to New Plan
            </button>
            <button
              onClick={handleCancel}
              className="w-full sm:w-auto flex-1 bg-[var(--color-danger)] hover:opacity-90 text-[var(--color-text-inverse)] px-6 py-3 rounded-xl font-semibold transition-all"
            >
              Cancel Subscription
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBillingView = () => (
    <div className="space-y-6">
      <div className="elevated-card p-6 rounded-2xl">
        <h3 className="text-xl font-semibold text-[var(--color-text)] mb-4">Payment History</h3>
        {paymentHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <tr>
                  <th className="p-3">Date</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Invoice</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((item) => {
                  // Find the subscription for this payment
                  const sub = currentSubscriptions.find(s => s.subscription_id === item.subscription_id);
                  // Find the plan for this subscription / payment
                  const plan = plans.find(p => p.id === (item.plan_id || sub?.plan_id));
                  // publicPlanLabel already includes "Plan" (e.g. "Basic Plan")
                  const planDisplayName = publicPlanLabel(plan?.name || item.plan_name || sub?.plan_name);
                  // The payment record is the source of truth for its billing cycle.
                  const paymentBillingCycle = item.billing_cycle || sub?.billing_cycle || 'monthly';
                  return (
                    <tr key={item.id} className="border-b border-gray-800/60">
                      <td className="p-3 text-[var(--color-text-secondary)] whitespace-nowrap">{formatDate(item.created_at)}</td>
                      <td className="p-3 text-[var(--color-text)] whitespace-nowrap">Subscription - {planDisplayName} ({paymentBillingCycle})</td>
                      <td className="p-3 text-[var(--color-text)] whitespace-nowrap">{formatPrice(item.amount)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          item.status === 'successful'
                            ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]'
                            : ['pending', 'unpaid'].includes(String(item.status).toLowerCase())
                              ? 'bg-[var(--color-warning)]/15 text-[var(--color-warning)]'
                              : 'bg-[var(--color-danger)]/15 text-[var(--color-danger)]'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <button 
                          onClick={() => handleDownloadInvoice(item.id)}
                          className="text-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors flex items-center"
                          disabled={downloadingInvoiceId === item.id}
                        >
                          {downloadingInvoiceId === item.id ? (
                            <svg className="animate-spin w-4 h-4 mr-2 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                            </svg>
                          ) : (
                            <Download className="w-4 h-4 mr-2" />
                          )}
                          PDF
                        </button>
                      </td>
                      <td className="p-3">
                        {isRetryablePayment(item) ? (
                          <button
                            type="button"
                            onClick={() => handleRetryPayment(item)}
                            disabled={retryingPaymentId === item.id}
                            className="inline-flex items-center rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <CreditCard className="mr-2 h-4 w-4" />
                            {retryingPaymentId === item.id
                              ? 'Opening payment...'
                              : ['pending', 'unpaid'].includes(String(item.status).toLowerCase())
                                ? 'Pay now'
                                : 'Retry payment'}
                          </button>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
            <h4 className="text-lg text-[var(--color-text)] mb-2">No Payment History</h4>
            <p className="text-[var(--color-text-muted)]">Your past payments will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full p-4">
      <div className="max-w-full mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)] mb-4 leading-tight">
            Subscription &{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
              Billing
            </span>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)]">
            Manage your plan, view payment history, and update billing details.
          </p>
        </div>

        <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-3xl p-6">
          <div className={isMobile ? "flex overflow-x-auto flex-nowrap border-b border-[var(--color-border)] mb-6 hide-scrollbar" : "flex border-b border-[var(--color-border)] mb-6"}>
            <button
              onClick={() => setActiveTab('my-subscription')}
              className={`min-w-max px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'my-subscription'
                  ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-text)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              My Subscription
            </button>
            <button
              onClick={() => setActiveTab('plans')}
              className={`min-w-max px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'plans'
                  ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-text)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              Plans
            </button>
            <button
              onClick={() => setActiveTab('payment-history')}
              className={`min-w-max px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'payment-history'
                  ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-text)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              Payment History
            </button>
          </div>

          <div>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-[var(--color-text-secondary)]">Loading subscription details...</p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'my-subscription' && renderCurrentSubscriptionView()}
                {activeTab === 'plans' && renderPlansView()}
                {activeTab === 'payment-history' && renderBillingView()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
